import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { authAPI } from '../services/api';
import { AuthUser, LoginCredentials } from '../types';
import { sanitizeObject } from '../utils/security';
import { ERROR_MESSAGES } from '../constants';
import { isApiError } from '../types/api-error';
import { logger } from '../utils/logger';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  hadSession: boolean;

  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  clearError: () => void;
  setUser: (user: AuthUser | null) => void;
  setHadSession: (value: boolean) => void;
  updateUserProfile: (profileData: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        _meInFlight: null as Promise<AuthUser> | null,
        _loginInFlight: null as Promise<void> | null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        hadSession: false,

        login: async (credentials: LoginCredentials) => {
          const current = (get() as any)._loginInFlight as Promise<void> | null;
          if (current) return current;

          const p = (async () => {
            try {
              set({ isLoading: true, error: null });

              // CPF: só dígitos. Senha: nunca passar por sanitizeText (pode corromper caracteres válidos).
              const loginPayload: LoginCredentials = {
                cpf: String(credentials.cpf ?? '').replace(/\D/g, ''),
                password: String(credentials.password ?? ''),
              };

              const loginResponse = await authAPI.login(loginPayload);

              if (loginResponse.success && loginResponse.data) {
                // Fonte de verdade: GET /user/me (sem fallback/merge tolerante).
                const user = await (async () => {
                  const currentMe = (get() as any)._meInFlight as Promise<AuthUser> | null;
                  if (currentMe) return currentMe;
                  const meP = (async () => {
                    const meResponse = await authAPI.getCurrentUser();
                    if (!meResponse.success || !meResponse.data?.user) {
                      throw new Error('Falha ao carregar perfil do usuário');
                    }
                    return meResponse.data.user;
                  })();
                  (get() as any)._meInFlight = meP;
                  try {
                    return await meP;
                  } finally {
                    (get() as any)._meInFlight = null;
                  }
                })();

                const sanitizedUser = {
                  ...user,
                  name: sanitizeObject({ name: user.name }).name,
                  email: sanitizeObject({ email: user.email || '' }).email,
                };

                set({
                  user: sanitizedUser,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null,
                  hadSession: true,
                });
              } else {
                throw new Error('Falha ao autenticar usuário');
              }
            } catch (error: unknown) {
              const message = getApiErrorMessage(error, ERROR_MESSAGES.UNKNOWN_ERROR);
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: message,
              });
              throw error;
            }
          })();

          (get() as any)._loginInFlight = p;
          try {
            return await p;
          } finally {
            (get() as any)._loginInFlight = null;
          }
        },

        logout: async () => {
          set({ isLoading: true });

          try {
            // Tenta chamar o backend, mas não falha se der erro
            // (pode estar offline ou token já expirado)
            await authAPI.logout();
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro desconhecido';
            logger.warn(
              '⚠️ [authStore] Erro ao chamar logout no backend (continuando com limpeza local):',
              message
            );
            // NÃO throw error aqui - sempre limpa o estado local
          } finally {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              hadSession: false,
            });

            // Limpa todos os stores do Zustand EXCETO o establishmentStore (configuração)
            try {
              const { useProfessionalsStore } = await import('./professionalsStore');
              const { useWorksStore } = await import('./worksStore');
              const { useBillingAccountsStore } = await import('./billingAccountsStore');
              const { useWaitingListStore } = await import('./waitingListStore');
              const { useDashboardStore } = await import('./dashboardStore');
              const { useSettingsStore } = await import('./settingsStore');
              const { useBillingStore } = await import('./billingStore');
              const { useAppointmentsStore } = await import('./appointmentsStore');

              // Limpa dados de profissionais, catálogo (works) e contas bancárias
              useProfessionalsStore.getState().clearProfessionals();
              useWorksStore.getState().clearWorks();
              useBillingAccountsStore.getState().clearAccounts();

              // Limpa lista de espera
              useWaitingListStore.getState().clearWaitingList();

              // Limpa dashboard
              useDashboardStore.getState().clearAll();

              // Limpa billing
              useBillingStore.getState().clearAll();

              // Limpa appointments
              useAppointmentsStore.getState().clearAppointments();

              // Limpa settings (mas NÃO limpa establishmentStore - configuração do estabelecimento)
              useSettingsStore.getState().clearAllSettings();

              // Todos os stores foram limpos no logout (exceto configuração do estabelecimento)
            } catch (error) {
              // Fallback: remove do localStorage manualmente
              try {
                localStorage.removeItem('hivi-professionals-storage');
                localStorage.removeItem('hivi-works-storage');
                localStorage.removeItem('hivi-billing-accounts-storage');
                localStorage.removeItem('hivi-waiting-list-storage');
                localStorage.removeItem('hivi-dashboard-storage');
                localStorage.removeItem('hivi-settings-storage');
                localStorage.removeItem('billing-storage');
                localStorage.removeItem('hivi-appointments-storage');
                // NÃO remove: hivi-establishment-storage (mantém configuração)

                // localStorage limpo manualmente no logout
              } catch (e) {
                logger.error('❌ Erro ao limpar localStorage:', e);
              }
            }
          }
        },

        checkAuth: async () => {
          try {
            set({ isLoading: true, error: null });
            const user = await (async () => {
              const current = (get() as any)._meInFlight as Promise<AuthUser> | null;
              if (current) return current;
              const p = (async () => {
                const response = await authAPI.getCurrentUser();
                if (!response.success || !response.data?.user) {
                  throw new Error('Dados inválidos');
                }
                return response.data.user;
              })();
              (get() as any)._meInFlight = p;
              try {
                return await p;
              } finally {
                (get() as any)._meInFlight = null;
              }
            })();

            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error: unknown) {
            // Sem fallback: qualquer falha em /me invalida a sessão no cliente.
            void error;
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          }
        },

        clearError: () => {
          set({ error: null });
        },

        setUser: (user: AuthUser | null) => {
          set({
            user,
            isAuthenticated: !!user,
          });
        },

        setHadSession: (value: boolean) => {
          set({ hadSession: value });
        },

        updateUserProfile: (profileData: Partial<AuthUser>) => {
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: {
                ...currentUser,
                ...profileData,
              },
            });
          }
        },

        /**
         * Atualiza o perfil via GET /me sem ligar `isLoading`.
         * Se `isLoading` ficasse true, `ProtectedRoute` / `PermissionBasedRoute` desmontam a árvore
         * (retornam null ou spinner full-screen) e estado local da página — ex.: modal do QR PIX — é perdido.
         */
        refreshUserData: async () => {
          try {
            const user = await (async () => {
              const current = (get() as any)._meInFlight as Promise<AuthUser> | null;
              if (current) return current;
              const p = (async () => {
                const response = await authAPI.getCurrentUser();
                if (!response.success || !response.data?.user) {
                  throw new Error('Dados inválidos');
                }
                return response.data.user;
              })();
              (get() as any)._meInFlight = p;
              try {
                return await p;
              } finally {
                (get() as any)._meInFlight = null;
              }
            })();

            set({
              user,
              isAuthenticated: true,
              error: null,
            });
          } catch (error: unknown) {
            // Sem fallback: falha ao atualizar /me mantém erro e não inventa um usuário parcial.
            const message =
              error instanceof Error ? error.message : 'Erro ao atualizar dados do usuário';
            set({
              error: message,
            });
          }
        },
      }),
      {
        name: 'hivi-auth-storage',
        partialize: (state) => ({
          user: state.user,
          hadSession: state.hadSession,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    {
      name: 'AuthStore',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);
